"use client";

import * as React from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./tabs";
import { cn } from "@/utils/utils";
import { Text } from "../information/text";
import { styleDrivers } from "@/utils/componentStyle";

export type VerticalTabItem = {
  value: string;
  label: React.ReactNode;
  content: React.ReactNode;
  disabled?: boolean;
};

export type VerticalTabsProps = {
  items: VerticalTabItem[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  className?: string;
  listClassName?: string;
  contentContainerClassName?: string;
  verticalText?: boolean;
};

export function VerticalTabs({
  items,
  value,
  defaultValue,
  onValueChange,
  className,
  listClassName,
  contentContainerClassName,
  verticalText = true,
}: VerticalTabsProps) {
  return (
    <Tabs
      orientation="vertical"
      value={value}
      defaultValue={defaultValue}
      onValueChange={onValueChange}
      className={cn(styleDrivers.verticalTabs.root, className)}
    >
      <TabsList className={cn(styleDrivers.verticalTabs.list, listClassName)}>
        {items.map((item) => (
          <TabsTrigger
            key={item.value}
            value={item.value}
            disabled={item.disabled}
            className={cn(styleDrivers.verticalTabs.trigger)}
          >
            {verticalText ? (
              <div className={cn(styleDrivers.verticalTabs.labelVertical)}>
                {typeof item.label === "string" || typeof item.label === "number" ? (
                  <Text as="p">{item.label}</Text>
                ) : (
                  item.label
                )}
              </div>
            ) : (
              typeof item.label === "string" || typeof item.label === "number" ? (
                <Text as="p">{item.label}</Text>
              ) : (
                item.label
              )
            )}
          </TabsTrigger>
        ))}
      </TabsList>
      <div className={cn(styleDrivers.verticalTabs.contentContainer, contentContainerClassName)}>
        {items.map((item) => (
          <TabsContent key={item.value} value={item.value} className={cn(styleDrivers.verticalTabs.content)}>
            {item.content}
          </TabsContent>
        ))}
      </div>
    </Tabs>
  );
}

// Example usage for the home preview
export function VerticalTabsDemo() {
  const items: VerticalTabItem[] = [
    {
      value: "profile",
      label: "Profile",
      content: (
        <div className="space-y-2">
          <Text as="h3" className="text-lg font-semibold">Profile</Text>
          <Text as="p" className="text-muted-foreground">
            Update your personal information, such as name, avatar and bio.
          </Text>
        </div>
      ),
    },
    {
      value: "security",
      label: "Security",
      content: (
        <div className="space-y-2">
          <Text as="h3" className="text-lg font-semibold">Security</Text>
          <Text as="p" className="text-muted-foreground">
            Manage your password and two‑factor authentication settings.
          </Text>
        </div>
      ),
    },
    {
      value: "notifications",
      label: "Notifications",
      content: (
        <div className="space-y-2">
          <Text as="h3" className="text-lg font-semibold">Notifications</Text>
          <Text as="p" className="text-muted-foreground">
            Choose how and when you want to be notified about activity.
          </Text>
        </div>
      ),
    },
  ];

  return <VerticalTabs defaultValue="profile" items={items} />;
}
